import React, { useState, useCallback } from 'react';
import { withTheme } from '@buoyhealth/common.buoy-theme';
import styled from 'styled-components';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { IPolarisInterviewQuestion } from 'src/packages/polaris/types/interviews';
import SubmitButton from './SubmitButton';
import { durationMultipliers } from '../utils';
import useTransition from '../hooks/useTransition';
import useIsPolaris from '../hooks/useIsPolaris';

const units = Object.keys(durationMultipliers);
type Unit = keyof typeof durationMultipliers;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  .MuiInputBase-input {
    font-size: 16px;
  }
`;

const DurationInput = ({
  onQuestionSubmit,
  question,
}: {
  onQuestionSubmit?: (questionIndex: number) => void;
  question?: IPolarisInterviewQuestion;
}) => {
  const transition = useTransition();
  const isPolaris = useIsPolaris();

  const [value, setValue] = useState(question?.answer?.[0]?.duration?.amount || '1');
  const [unit, setUnit] = useState<Unit>(question?.answer?.[0]?.duration?.unit || 'day');
  const [formErrors, setFormErrors] = useState({ val: false });

  const handleValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setFormErrors({ val: false });
    }
    setValue(e.target.value as string);
  }, []);

  const handleUnitChange = useCallback((e: SelectChangeEvent) => {
    setUnit(e.target.value as Unit);
  }, []);

  const handleSubmit = useCallback(() => {
    const number = Number(value);
    const isValidNumber = !Number.isNaN(number) && number > 0;

    if (!isValidNumber) {
      return setFormErrors({ val: !isValidNumber });
    }

    if (question?.index !== undefined) {
      onQuestionSubmit?.(question.index);
    }

    transition({
      data: {
        options: [
          {
            ...(question?.options?.[0] || []),
            duration: {
              amount: `${value}`,
              unit,
            },
          },
        ],
        question,
        isPolaris,
      },
    });
  }, [isPolaris, onQuestionSubmit, question, transition, unit, value]);

  return (
    <>
      <Row>
        <TextField
          error={formErrors.val}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            type: 'number',
            sx: {
              height: 'auto',
            },
          }}
          onChange={handleValueChange}
          sx={{ backgroundColor: 'white', borderRadius: '8px' }}
          type="number"
          value={value}
        />
        <Select
          fullWidth
          onChange={handleUnitChange}
          sx={{ backgroundColor: 'white', marginLeft: 1 }}
          value={unit}
          MenuProps={{
            sx: {
              zIndex: 10000000000,
            },
          }}
        >
          {units.map((un) => (
            <MenuItem key={un} value={un}>
              {value !== '1' ? `${un}s` : un}
            </MenuItem>
          ))}
        </Select>
      </Row>
      <SubmitButton
        errorMessage="Please correct the errors above."
        isError={formErrors.val}
        onClick={handleSubmit}
      />
    </>
  );
};

export default withTheme(DurationInput);
